import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BaseFormDialogComponent } from '@hub/common/BaseFormDialogComponent';
import { DateFunctions } from '@hub/common/DateFunctions';
import { Functions } from '@hub/common/Functions';
import { eActionState } from '@hub/generated/enums';
import { FilesController } from '@hub/generated/services';
import { Attachment } from '@hub/models/attachment.model';
import {
  CurrentUserService,
  FormEventService,
  FormService,
  HttpClientService,
  NotificationService,
  PageLoaderService,
} from 'app/services';
import { ConfirmationService, SelectItem } from 'primeng/api';

@Component({
  selector: "hub-attachment-dialog",
  templateUrl: "./attachment-dialog.component.html"
})
export class AttachmentDialogComponent extends BaseFormDialogComponent
  implements OnInit, OnDestroy, OnChanges {
  private static fileToUpload: File;

  constructor(
    protected fb: FormBuilder,
    protected formService: FormService,
    protected notificationService: NotificationService,
    protected formEventService: FormEventService,
    protected pageLoaderService: PageLoaderService,
    protected currUserService: CurrentUserService,
    protected confirmationService: ConfirmationService,
    protected httpService: HttpClientService,
    protected filesController: FilesController
  ) {
    super(
      fb,
      formService,
      formEventService,
      notificationService,
      pageLoaderService,
      confirmationService
    );
  }

  @Input()
  isNew: boolean = true;
  @Input()
  selection: any[];
  @Input()
  contactId;
  @Input()
  attachmentTypeEnum: any;
  @Output()
  updateAttachmentEmitter: EventEmitter<Attachment> = new EventEmitter();
  @Output()
  notificationEmitter: EventEmitter<string> = new EventEmitter();

  formGroup: FormGroup;
  attachmentVisible: boolean;
  isFormEdit: boolean;
  types: SelectItem[];
  attachment: any;
  requiredValidationMessage: string = "Required";
  isSaveClicked: boolean = false;
  private clientId: string;

  ngOnInit() {
    this.onInit();
  }

  ngOnChanges() {
    try {
      if (this.attachmentTypeEnum) {
        this.types = Functions.IterateEnum(this.attachmentTypeEnum).map(v => {
          return {
            label: v.label.trim(),
            value: v.value
          };
        });
      }
    } catch (error) {}
  }

  ngOnDestroy() {
    this.ngInitUnsubscribe.next();
    this.ngInitUnsubscribe.complete();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  public formInit(): void {
    AttachmentDialogComponent.fileToUpload = null;

    this.isFormEdit = false;
    this.formService.formEdit(false);

    this.formGroup = this.fb.group({
      Errors: [""],
      Name: ["", Validators.required],
      Type: [
        this.attachmentTypeEnum.NotSet,
        Validators.compose([Validators.required, this.checkIfNotSet])
      ],
      Description: [""],
      FileId: [""],
      User: [""]
    });
    AttachmentDialogComponent.fileToUpload = null;

    this.attachment = this.selection[0];

    if (!this.isNew) {
      this.attachment = this.selection[0];
      this.formGroup.patchValue({
        Id: this.attachment.Id,
        AssocId: this.attachment.AssocId,
        Type: Functions.getEnumValue(
          this.attachmentTypeEnum,
          this.attachment.Type
        ),
        Name: this.attachment.Name,
        Description: this.attachment.Description,
        FileId: this.attachment.FileId,
        User: this.attachment.User
      });
    } else {
      this.formGroup.patchValue({
        Id: null,
        AssocId: null,
        Type: this.attachmentTypeEnum.NotSet,
        Name: "",
        Description: ""
      });
    }
  }

  public saveActionHandler(e: any): void {
    if (this.formGroup.valid) {
     // this.pageLoaderService.showPageLoader();

      if (this.isNew && !!AttachmentDialogComponent.fileToUpload) {
        this.filesController.UploadPublicFile(AttachmentDialogComponent.fileToUpload)
          .subscribe(
            result => {
              let a = this.getAttachmentFormValues();
              a.FileId = result.FileId;
              a.ActionState = eActionState.Inserted;
              this.updateClientAttachments(a);
            },
            err => {
             // this.pageLoaderService.hidePageLoader();
              this.notificationService.error("UploadError");
            }
          );
      } else if (!this.isNew && !!AttachmentDialogComponent.fileToUpload) {
        this.filesController.UploadPublicFile(AttachmentDialogComponent.fileToUpload)
          .subscribe(
            result => {
              let a = this.getAttachmentFormValues();
              a.FileId = result.FileId;
              a.ActionState = eActionState.Changed;
              this.updateClientAttachments(a);
            },
            () => {
             // this.pageLoaderService.hidePageLoader();
              this.notificationService.error("UploadError");
            }
          );
      } else {
        if (!!this.formGroup.controls["FileId"].value) {
          let a = this.getAttachmentFormValues();
          a.FileId = this.formGroup.controls["FileId"].value;
          a.ActionState = eActionState.Changed;
          this.updateClientAttachments(a);
        } else {
          this.pageLoaderService.hidePageLoader();
          this.notificationService.error("No File");
        }
      }
    }
  }

  public onAcceptConfirmationHandler(): void {}

  public onCloseInvalidFormHandler(): void {}

  public formValueChangeHandler(data: any): void {
    if (this.isFormEdit === false) {
      this.formService.formEdit(true);
      this.isFormEdit = true;
    }
  }

  public save() {
    this.isSaveClicked = true;
    let body = document.getElementsByTagName("body")[0];
    body.classList.remove("overflow-hidden-scroll");
    body.classList.add("overflow-show-scroll");
    this.formService.saveClick();
  }

  updateClientAttachments(a) {
    this.updateAttachmentEmitter.emit(a);
    this.hide();
  }

  public cancel() {
    this.hide();
    let body = document.getElementsByTagName("body")[0];
    body.classList.remove("overflow-hidden-scroll");
    body.classList.add("overflow-show-scroll");
   // this.pageLoaderService.hidePageLoader();
  }

  afterShow() {
    let body = document.getElementsByTagName("body")[0];
    body.classList.remove("overflow-show-scroll");
    body.classList.add("overflow-hidden-scroll");
  }

  public fileUpload(file: File) {
    AttachmentDialogComponent.fileToUpload = file;
  }

  public getFileName() {
    if (AttachmentDialogComponent.fileToUpload) {
      return AttachmentDialogComponent.fileToUpload.name;
    } else {
      return "";
    }
  }

  public getFileIcon() {
    return AttachmentDialogComponent.fileToUpload ? "pi pi-file" : "";
  }

  private CanDeactivate(): boolean {
    return !this.isFormEdit;
  }

  private getAttachmentFormValues(): any {
    const val = this.formGroup.value;
    const editAttachment: Attachment = {
      ActionState: eActionState.NotSet,
      Id: this.attachment.Id,
      AssocId: this.attachment.AssocId,
      ContactId: this.contactId,
      Name: val.Name,
      Type: Functions.getEnumName(this.attachmentTypeEnum, val.Type),
      Description: val.Description,
      Date: DateFunctions.adjustDate(new Date(), true),
      SizeInBytes: 0,
      FileId: null,
      User: val.User
    };

    return editAttachment;
  }
  private checkIfNotSet(control: FormControl) {
      let selectedEnum = control.value;
      if (selectedEnum === 0) {
        return {
          required: true
        };
      }

      return null;
  }
}
